import { Case, Switch } from "#root/components/conditionals";
import * as Fields from "#root/components/form/fields";
import { GlyphLoading } from "#root/components/glyphs";
import StandardEditor from "#root/components/standard-editor";
import { type DeploymentListItem, getDeploymentCollectionForAccount } from "#root/schema/deployment";
import { useData } from "#root/utility/hooks";
import { mergeUpdate } from "#root/utility/merge-update";
import type { RecordModificationResponse } from "#root/utility/remote-data";
import type { ReactNode } from "react";
import { useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageLayout from "./layout";

export default function ({ accountId, deploymentId }): ReactNode {
  const navigate = useNavigate();
  const collection = useMemo(() => getDeploymentCollectionForAccount(accountId), [accountId]);

  const initialData = useMemo(
    () => ({
      accountId,
    }),
    [accountId],
  );

  const [deployments, setDeployments] = useData(async () => {
    const { rows } = await collection.list();
    return rows;
  }, [accountId]);

  const handleUpdate = useCallback(
    (data: RecordModificationResponse<DeploymentListItem>) => {
      setDeployments(mergeUpdate(deployments!, data));

      if ("deleted" in data) {
        navigate(`/admin/account/${accountId}/deployment`);
      } else if ("created" in data) {
        navigate(`/admin/account/${accountId}/deployment/${data.created}`);
      }
    },
    [navigate, accountId, setDeployments, deployments],
  );

  if (!deployments) {
    return (
      <PageLayout title="Admin - Deployments">
        <section>
          <GlyphLoading/>
        </section>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Admin - Deployments">
      <section>
        <Switch>
          <Case expr={deploymentId == null}>
            <button className="flat center" type="button" onClick={() => navigate(`/admin/account/${accountId}/deployment/new`)}>
              New Deployment
            </button>
            <h2>Deployments</h2>
            <DeploymentList accountId={accountId} deployments={deployments}/>
          </Case>
          <Case expr={deploymentId === "new"}>
            <h1>New Deployment - Account {accountId}</h1>
            <DeploymentEditor collection={collection} initialData={initialData} onSave={handleUpdate}/>
          </Case>
          <Case expr={deploymentId != null}>
            <h1>Edit Deployment - Account {accountId}</h1>
            <DeploymentEditor id={deploymentId} collection={collection} onSave={handleUpdate} onDelete={handleUpdate}/>
          </Case>
        </Switch>
      </section>
    </PageLayout>
  );
}

function DeploymentList({ accountId, deployments }) {
  if (!deployments) {
    return <GlyphLoading/>;
  }

  return (
    <table className="deployment-list">
      <thead>
      <tr>
        <th>Ref</th>
        <th>Name</th>
        <th>Status</th>
        <th>Action</th>
      </tr>
      </thead>
      <tbody>
      {deployments.map(({ id, name, status }) => (
        <tr key={id}>
          <td>CD-{id}</td>
          <td>{name}</td>
          <td>{status}</td>
          <td>
            <Link to={`/admin/account/${accountId}/deployment/${id}`}>Edit</Link>&nbsp;&middot;&nbsp;
            <Link to={`/admin/account/${accountId}/deployment/${id}/usage`}>Usage</Link>&nbsp;&middot;&nbsp;
            <Link to={`/admin/account/${accountId}/deployment/${id}/entitlement`}>Entitlements</Link>&nbsp;&middot;&nbsp;
            <Link to={`/admin/account/${accountId}/deployment/${id}/credential`}>Credentials</Link>
          </td>
        </tr>
      ))}
      </tbody>
    </table>
  );
}

function DeploymentEditor(props) {
  return (
    <StandardEditor {...props}>
      <Fields.Set name="name">
        <label>Deployment Name</label>
        <Fields.FullName required/>
        <Fields.Validity/>
      </Fields.Set>
      <Fields.Set name="status">
        <label>Deployment Status</label>
        <Fields.Select>
          <Fields.Option value="enabled">Enabled</Fields.Option>
          <Fields.Option value="disabled">Disabled</Fields.Option>
        </Fields.Select>
        <Fields.Validity/>
      </Fields.Set>
    </StandardEditor>
  );
}
